/**
 * Browser compatibility issues and error handling are difficult to test. Testing is
 * currently done in local dev through BrowserStack.
 *
 * See https://www.notion.so/withpersona/Browser-compatibility-issues-cf2ab5b41e3b418fbd5d656e0de18560
 */
import { Color, Spacing } from '@persona-id/design-tokens';
import { Box, FaceLandmarks68 } from '@vladmandic/face-api';
import { CustomModelClient } from 'obfuscated/inquiry/lib/clients/CustomModel/CustomModelClient';
import {
  DEFAULT_CAMERA_INSPECTION_DATA,
  generateDeviceData,
  generateStreamSettingsData,
  generateStreamStability,
  getStreamStabilitySnapshotHash,
} from 'obfuscated/inquiry/lib/utils/camera-inspection';
import { Component, LegacyRef, MouseEventHandler } from 'react';
import { useTranslation } from 'react-i18next';
import Webcam from 'react-webcam';
import styled from 'styled-components';

import Button, { ButtonType } from 'inquiry/components/Button';
import { FeatureFlagsMap } from 'inquiry/lib/interfaces/feature-flags';
import EventsClient from 'lib/clients/EventsClient';
import { FaceApiClient } from 'lib/clients/FaceApiClient';
import FrameTrigger from 'lib/clients/FrameTrigger';
import FrameTriggerClient from 'lib/clients/FrameTriggerClient';
import { CapturedVideoData, MediaRecorderManager } from 'lib/clients/mediaRecorder';
import { TrackingEventsClient } from 'lib/clients/TrackingEventsClient';
import { VideoClient } from 'lib/clients/VideoClient';
import { colorCss, StyleValueColorConfig } from 'lib/inquiryStyles';
import {
  PoseConfig,
  SerializedButtonStyles,
  SerializedStepSelfieStyles,
  StepSelfieConfig,
} from 'lib/interfaces/StepsConfig';
import { captureException, captureMessage } from 'lib/reporting';
import { createCanvas } from 'lib/utils/canvas';
import {
  generateWebGLDebugInfo,
  isIOS,
  isIOSDoordashUserAgent,
  isMobile,
  isSafari as deviceIsSafari,
  isSafari,
} from 'lib/utils/device';
import { isInCypress } from 'lib/utils/dom';
import { FullScreenLoadingSpinner } from 'verify/components/LoadingSpinner';
import PositionableButtonContainer from 'verify/components/PositionableButtonContainer';
import { HolderClient } from 'verify/lib/clients/HolderClient';
import { CameraInspectionData } from 'verify/lib/interfaces/cameraInspection';
import { SelfieVideoCaptureMethod } from 'verify/lib/interfaces/CaptureOption';
import {
  CapturedSelfie,
  CapturedSelfiesSubmitData,
  SelfiePoseOrder,
  SelfieStep,
} from 'verify/lib/interfaces/selfie';
import { getCurrentSelfieStep, getSelfiePose, getSelfieStepConfig } from 'verify/lib/selfie';
import { BLANK_CAPTURE_BYTE_SIZE } from 'verify/lib/utils/webcam';
import { CaptureMethod } from 'verify/models/verification';
import {
  createFrameTriggerClient,
  horizontalAngleFromLandmarks,
  SelfieTriggerParams,
  SelfieTriggerType,
  triggerTypeToSelfiePose,
} from 'verify/scanners/SelfieScanner/client';
import { BaseHintPrompt, HintPrompt } from 'verify/scanners/SelfieScanner/HintPrompt';
import ProgressOverlay from 'verify/scanners/SelfieScanner/ProgressOverlay';
import { VideoDimensionsTracker } from 'verify/scanners/SelfieScanner/VideoDimensionsTracker';

// Params chosen here:
// https://www.notion.so/withpersona/Tighten-TinyFaceDetector-options-for-SelfieScanner-vs-GovernmentIdScanner-8afcf7e20823478aa25ea95a60da00d2
const FACE_DETECTION_INPUT_SIZE = 320;
const FACE_DETECTION_THRESHOLD = 0.4;
const CANVAS_NARROW_MINIMUM_TIME = 500;

const VIDEO_MAX_RECORDING_TIME = 120_000;

// Was previously 100. Increased to 250 to reduce resource consumption.
const DETECT_INTERVAL = 250;
const DEFAULT_PROFILE_SIZE = 250;
const VIDEO_ELEMENT_HEIGHT = 250;
const VIDEO_BLURRER_BORDER = 5;
const FOCUSED_TRIGGER_TYPES = [
  SelfieTriggerType.Verifying,
  SelfieTriggerType.TooFar,
  SelfieTriggerType.TooClose,
  SelfieTriggerType.NotCentered,
  SelfieTriggerType.Left,
  SelfieTriggerType.Right,
  SelfieTriggerType.LeftDelayed,
  SelfieTriggerType.RightDelayed,
  SelfieTriggerType.Center,
  SelfieTriggerType.LeftCapture,
  SelfieTriggerType.RightCapture,
  SelfieTriggerType.CenterCapture,
];

// rough p95 value of getUserMedia load times
// amplitude graph: https://app.amplitude.com/analytics/persona/chart/uyvrxhs9
const DELAY_BEFORE_SHOWING_SPINNER_FOR_NON_VIDEO = 1350;

const Container = styled('div')`
  position: relative;
  max-width: 100%;
  margin: 0 auto;
`;

const VideoWrapper = styled('div')`
  position: relative;
  width: 100%;
  /* Hide the "glow" from blur filter */
  overflow: hidden;
  display: flex;
  justify-content: center;
  align-items: center;

  video {
    transform: scaleX(-1);
    flex: 0 1 auto;
  }
`;

const OverlayPositioner = styled('div')`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
`;

const ButtonStyled = styled(Button)`
  margin-top: ${Spacing[4]};
  margin-bottom: ${Spacing[5]};
`;

const EditorPreviewVideoPlaceholder = styled.div`
  background-color: ${Color.CoreSlate400};
  width: 100%;
  // add 10 to match the border for the video blurrer which is included in the placeholder
  height: ${VIDEO_ELEMENT_HEIGHT + VIDEO_BLURRER_BORDER * 2}px;
`;

interface VideoBlurrerProps {
  isActive: boolean;
  backgroundColor?: StyleValueColorConfig;
}

const VideoBlurrer = styled('div')<VideoBlurrerProps>`
  /* Without this, <video> will have some whitespace at bottom */
  line-height: 0;
  transition: filter 0.5s;
  filter: ${(props) => (props.isActive ? 'blur(5px)' : '')};
  /* NB: HACK to cover up the "glow" */
  border-width: ${VIDEO_BLURRER_BORDER}px;
  border-style: solid;
  border-color: ${({ isActive, backgroundColor, theme }) =>
    isActive ? `${colorCss(backgroundColor) ?? theme.colorModalBackground}` : 'transparent'};
`;

const LoadingSpinnerWrapper = styled.div<{ backgroundColor?: StyleValueColorConfig }>`
  background-color: ${({ backgroundColor, theme }) =>
    `${colorCss(backgroundColor) ?? theme.colorModalBackground}`};
  position: absolute;
  width: 100%;
  padding-bottom: 100%;
  z-index: 1;
`;

interface SelfieScannerProps {
  audioEnabled: boolean;
  assets?: StepSelfieConfig['assets']['record-page'];
  localizations?: StepSelfieConfig['localizations']['capture-page'];
  capturedSelfies: CapturedSelfie[];
  clearCapturedSelfies: () => void;
  debug?: boolean;
  delayUntilManualCaptureMs?: number | null;
  handleFailed: (error: Error | string, state: 'load' | 'capture') => void;
  handleComplete: (capturedSelfiesSubmitData: CapturedSelfiesSubmitData) => void;
  detectTimeout?: number;
  handleSlowFaceDetection: () => void;
  handleRestartStep?: (handleRestartStepArgs?: { timeExceeded?: boolean }) => void;
  poseOrder: SelfiePoseOrder;
  poseConfigs?: PoseConfig[];
  saveCapturedSelfie: (
    selfieStep: SelfieStep,
    imageDataUrl: string,
    captureMethod: CaptureMethod,
    capturedAt: string,
  ) => void;
  videoConstraints: MediaTrackConstraints;
  requireHigherAccuracyCapture: boolean;
  featureFlags: FeatureFlagsMap;
  styles?: SerializedStepSelfieStyles;
  buttonStyles?: SerializedButtonStyles;
  enableVideoRecording?: boolean;
  uploadProgress?: number;
  videoSessionJwt?: string | null;
  videoCaptureMethods?: SelfieVideoCaptureMethod[];
  enableMobilePendingExperienceForWeb: boolean;
}

interface SelfieScannerState {
  profileSize: number;
  activeTrigger: FrameTrigger<
    SelfieTriggerType,
    SelfieTriggerParams,
    StepSelfieConfig['localizations']['capture-page']
  >;
  autoCaptureDisabled: boolean;
  hasSentSelfieData: boolean;
  hasUnmounted: boolean;
  manualVisible: boolean;
  videoWidthOverride?: number;
  videoHeightOverride?: number;
  tickCount: number;
  capturedVideoData?: CapturedVideoData;
  mediaRecorderSubId?: string;
  hasStoppedRecording: boolean;
  showPreloadingSpinnerVideo: boolean;
  showPreloadingSpinnerForUserMedia: boolean;
  readyToCapture: boolean;
  startTime: number;
  videoDimensions: { width: number; height: number } | undefined;
  currentStep?: SelfieStep;
  allowNarrowCanvas?: boolean;
}

interface SelfieScannerBaseProps {
  assets?: StepSelfieConfig['assets']['record-page'];
  localizations?: StepSelfieConfig['localizations']['capture-page'];
  currentStep?: SelfieStep;
  poseOrder: SelfiePoseOrder;
  activeTrigger: FrameTrigger<
    SelfieTriggerType,
    SelfieTriggerParams,
    StepSelfieConfig['localizations']['capture-page']
  >;
  videoWidthOverride?: number;
  videoHeightOverride?: number;
  setWebcamRef: LegacyRef<Webcam>;
  videoConstraints: MediaTrackConstraints;
  handleUserMedia: (stream: MediaStream) => void;
  handleFailed: (error: Error | string, state: 'load' | 'capture') => void;
  handleManualCapture: MouseEventHandler<HTMLButtonElement>;
  manualVisible: boolean;
  profileSize: number;
  debug?: boolean;
  isEditorPreview?: boolean;
  styles?: SerializedStepSelfieStyles;
  buttonStyles?: SerializedButtonStyles;
  audioEnabled: boolean;
  showPreloadingSpinner?: boolean;
  showPreloadingHint?: boolean;
  uploadProgress?: number;
  enableMobilePendingExperienceForWeb: boolean;
  autoCaptureDisabled: boolean;
  featureFlags: FeatureFlagsMap;
  allowNarrowCanvas?: boolean;
}

export const SelfieScannerBase = ({
  audioEnabled,
  autoCaptureDisabled,
  assets,
  localizations,
  currentStep,
  poseOrder,
  activeTrigger,
  videoWidthOverride,
  videoHeightOverride,
  setWebcamRef,
  videoConstraints,
  handleUserMedia,
  handleFailed,
  handleManualCapture,
  manualVisible,
  profileSize,
  debug,
  isEditorPreview,
  styles,
  buttonStyles,
  showPreloadingSpinner,
  showPreloadingHint,
  uploadProgress,
  enableMobilePendingExperienceForWeb,
  allowNarrowCanvas,
}: SelfieScannerBaseProps) => {
  const isNarrowedOverlay =
    !!allowNarrowCanvas &&
    (FOCUSED_TRIGGER_TYPES.indexOf(activeTrigger.getType()) !== -1 || manualVisible);
  const [t] = useTranslation();

  const showDelayedHint =
    [
      SelfieTriggerType.LeftDelayed,
      SelfieTriggerType.RightDelayed,
      SelfieTriggerType.Left,
      SelfieTriggerType.Right,
    ].indexOf(activeTrigger.getType()) !== -1;

  const isComplete = currentStep == null;
  const manualDisabled =
    (isComplete ||
      ![
        SelfieTriggerType.Center,
        SelfieTriggerType.Empty,
        SelfieTriggerType.LeftDelayed,
        SelfieTriggerType.RightDelayed,
      ].includes(activeTrigger.getType())) &&
    !autoCaptureDisabled;

  const videoBlurActive = showDelayedHint || isComplete;

  return (
    <Container data-test='selfie-scanner'>
      {!showPreloadingSpinner && (
        <HintPrompt
          activeTrigger={activeTrigger}
          styles={styles?.captureHintText}
          localizations={localizations}
        />
      )}
      {showPreloadingHint && (
        <BaseHintPrompt
          styles={styles?.captureHintText}
          hintText={t('selfie-scanner.hint.loading-camera')}
        />
      )}
      {showPreloadingSpinner && (
        <LoadingSpinnerWrapper backgroundColor={styles?.base?.backgroundColor}>
          <FullScreenLoadingSpinner />
        </LoadingSpinnerWrapper>
      )}
      <VideoWrapper style={{ maxHeight: videoHeightOverride }}>
        {isEditorPreview ? (
          <EditorPreviewVideoPlaceholder data-test='selfie-scanner__editor-preview-video-placeholder' />
        ) : (
          <VideoBlurrer
            // TODO: [PER-23734] This is apparently causing issues on iOS 17.
            isActive={videoBlurActive}
            backgroundColor={styles?.base?.backgroundColor}
          >
            <Webcam
              audio={audioEnabled}
              width={videoWidthOverride ?? '100%'}
              height='initial'
              ref={setWebcamRef}
              videoConstraints={videoConstraints}
              onUserMedia={handleUserMedia}
              onUserMediaError={(error) => handleFailed(error, 'load')}
            />
          </VideoBlurrer>
        )}
        <OverlayPositioner>
          <ProgressOverlay
            assets={assets}
            showDelayedHint={showDelayedHint}
            isNarrowed={isNarrowedOverlay}
            profileSize={profileSize}
            currentStep={currentStep}
            poseOrder={poseOrder}
            styles={styles}
            uploadProgress={uploadProgress}
            showLoadingCircle={true}
            enableMobilePendingExperienceForWeb={!!enableMobilePendingExperienceForWeb}
          />
        </OverlayPositioner>
      </VideoWrapper>

      {manualVisible && (
        <PositionableButtonContainer>
          <ButtonStyled
            id='selfie-scanner__capture--manual'
            data-test='selfie-scanner__capture--manual'
            onClick={handleManualCapture}
            disabled={manualDisabled}
            styles={buttonStyles?.[ButtonType.Primary] || styles?.buttons?.primary}
          >
            {t('selfie-scanner.prompt.btn-capture-manual')}
          </ButtonStyled>
        </PositionableButtonContainer>
      )}

      {debug && <canvas style={{ width: '100%' }} id='debugLandmarks' />}
    </Container>
  );
};

export default class SelfieScanner extends Component<SelfieScannerProps, SelfieScannerState> {
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  private refWebcam: Webcam & HTMLVideoElement;
  private timerDetect: number | undefined;
  private timerManualShow: number | undefined;
  private timerShowSpinnerForNonVideo: number | undefined;
  private timerNarrowCanvasEnable: number | undefined;

  private faceCanvasBuffer: HTMLCanvasElement;
  private cleanupFaceCanvasBuffer: () => void;
  private canvasBuffer: HTMLCanvasElement;
  private cleanupCanvasBuffer: () => void;
  private hintTriggerClient: FrameTriggerClient<
    SelfieTriggerType,
    SelfieTriggerParams,
    StepSelfieConfig['localizations']['capture-page']
  >;
  private shouldCaptureCameraInspectionSnapshots: boolean;
  /**
   * An array of hashes of images taken from the video feed to determine stream stability.
   * We hash the images because storing multiple images in memory can lead to OOMs.
   */
  private cameraInspectionSnapshots: string[];
  private cameraInspectionData?: CameraInspectionData;

  private tickCountWhenFeedIsReady: number;

  constructor(props: SelfieScannerProps) {
    super(props);

    const currentStep = getCurrentSelfieStep(props.capturedSelfies, props.poseOrder);
    const poseConfig = currentStep && getSelfieStepConfig(currentStep, props.poseConfigs);

    this.hintTriggerClient = createFrameTriggerClient(props.poseOrder);

    const autoCaptureDisabled =
      poseConfig?.['auto-capture-enabled'] === false &&
      !!this.props.featureFlags['inquiry:dynamic-selfie-config'];

    const isManualVisible =
      isInCypress() || !!this.props.featureFlags['inquiry:dynamic-selfie-config']
        ? poseConfig?.['manual-capture-delay-ms'] === 0 || autoCaptureDisabled
        : props.delayUntilManualCaptureMs === 0;

    this.state = {
      profileSize: DEFAULT_PROFILE_SIZE,
      activeTrigger: this.hintTriggerClient.getTriggered(),
      autoCaptureDisabled: autoCaptureDisabled,
      hasSentSelfieData: false,
      hasUnmounted: false,
      manualVisible: isManualVisible,
      videoWidthOverride: undefined,
      videoHeightOverride: undefined,
      tickCount: 0,
      capturedVideoData: undefined,
      mediaRecorderSubId: undefined,
      hasStoppedRecording: false,
      showPreloadingSpinnerVideo: !!props.enableVideoRecording,
      showPreloadingSpinnerForUserMedia: false,
      readyToCapture: !props.enableVideoRecording,
      startTime: Date.now(),
      videoDimensions: undefined,
      currentStep: currentStep,
    };

    const [faceCanvasBuffer, cleanupFaceCanvasBuffer] = createCanvas();
    const [canvasBuffer, cleanupCanvasBuffer] = createCanvas();
    this.faceCanvasBuffer = faceCanvasBuffer;
    this.cleanupFaceCanvasBuffer = cleanupFaceCanvasBuffer;
    this.canvasBuffer = canvasBuffer;
    this.cleanupCanvasBuffer = cleanupCanvasBuffer;

    this.cameraInspectionSnapshots = [];
    this.shouldCaptureCameraInspectionSnapshots = true;
    this.cameraInspectionData = undefined;

    /**
     * This should be reset in the resizeVideo callback, but we default it to a high-ish default
     * so we don't call FaceAPI too early and (onwards is hypothesis) trigger the async call when
     * the feed is in a bad state of being tiny
     */
    this.tickCountWhenFeedIsReady = 30;
  }

  public updatePoseManualCaptureTimer(currentStep?: SelfieStep) {
    if (!this.props.featureFlags['inquiry:dynamic-selfie-config']) {
      if (this.props.delayUntilManualCaptureMs && this.props.delayUntilManualCaptureMs > 0) {
        this.timerManualShow = window.setTimeout(() => {
          this.setState({ manualVisible: true });
        }, this.props.delayUntilManualCaptureMs || 0);
      }
      return;
    }

    this.timerManualShow && clearTimeout(this.timerManualShow);
    let manualCaptureDelayMs = this.props.delayUntilManualCaptureMs;
    if (currentStep) {
      const poseConfig = getSelfieStepConfig(currentStep, this.props.poseConfigs);
      manualCaptureDelayMs = poseConfig?.['manual-capture-delay-ms'] ?? manualCaptureDelayMs;
    }
    if (manualCaptureDelayMs && manualCaptureDelayMs > 0) {
      this.timerManualShow = window.setTimeout(() => {
        this.setState({ manualVisible: true });
      }, manualCaptureDelayMs);
    } else if (manualCaptureDelayMs === 0) {
      this.setState({ manualVisible: true });
    }
  }

  public onSelfieStepChange() {
    const { capturedSelfies, poseOrder } = this.props;
    const currentStep = getCurrentSelfieStep(capturedSelfies, poseOrder);
    const poseConfig = this.props.poseConfigs?.find((config) => config.pose === currentStep);
    if (!this.props.featureFlags['inquiry:dynamic-selfie-config']) {
      return this.setState({
        currentStep,
      });
    }

    const autoCaptureDisabled =
      poseConfig?.['auto-capture-enabled'] === false &&
      !!this.props.featureFlags['inquiry:dynamic-selfie-config'];

    this.setState({
      currentStep,
      autoCaptureDisabled,
      manualVisible: false,
    });

    if (
      poseConfig &&
      poseConfig['manual-capture-enabled'] === false &&
      poseConfig['auto-capture-enabled'] !== false
    ) {
      this.setState({ manualVisible: false });
      clearTimeout(this.timerManualShow);
    } else {
      this.updatePoseManualCaptureTimer(currentStep);
    }
  }

  public componentDidUpdate() {
    // NOTE: This is in componentDidUpdate because capturedSelfies needs to propagate through Redux
    // We should rework this to not be so indirect
    const { capturedSelfies, poseOrder } = this.props;
    const { hasSentSelfieData } = this.state;
    const currentStep = getCurrentSelfieStep(capturedSelfies, poseOrder);
    if (currentStep !== this.state.currentStep) {
      this.onSelfieStepChange();
    }

    if (currentStep == null && !hasSentSelfieData) {
      if (!this.props.enableVideoRecording) {
        this.completeSelfieScan();
      } else {
        // NOTE: if we don't have the video data yet, that means we need to call STOP
        // we only get the final chunks when the STOP event occurs on MediaRecorder
        if (!this.state.hasStoppedRecording) {
          MediaRecorderManager.currentClient?.stopRecording();
          this.setState({ hasStoppedRecording: true });
        }
      }
    }
  }

  public componentDidMount = async () => {
    if (this.props.debug) {
      console.info(generateWebGLDebugInfo());
    }

    try {
      await FaceApiClient.loadModels();
    } catch (err) {
      // NOTE: Some Android devices fail to compile the fragment shader.
      // If many devices hit this, consider following recommendation here:
      // https://github.com/tensorflow/tfjs/issues/952

      // Here we send them to the stillfie flow.
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      this.props?.handleFailed(err, 'load');
      return;
    }

    this.runTick();

    this.updatePoseManualCaptureTimer(this.state.currentStep);

    this.timerNarrowCanvasEnable = window.setTimeout(() => {
      this.setState({ allowNarrowCanvas: true });
    }, CANVAS_NARROW_MINIMUM_TIME);

    this.timerShowSpinnerForNonVideo = window.setTimeout(() => {
      this.setState({ showPreloadingSpinnerForUserMedia: true });
    }, DELAY_BEFORE_SHOWING_SPINNER_FOR_NON_VIDEO);
  };

  public componentWillUnmount() {
    this.setState({
      ...this.state,
      hasUnmounted: true,
    });
    clearTimeout(this.timerDetect);
    clearTimeout(this.timerManualShow);
    clearTimeout(this.timerShowSpinnerForNonVideo);
    clearTimeout(this.timerNarrowCanvasEnable);
    VideoClient.dispose();

    if (this.props.enableVideoRecording) {
      MediaRecorderManager.destroy('selfie');
    }

    this.cleanupCanvasBuffer();
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    this.canvasBuffer = null;
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    this.cleanupCanvasBuffer = null;

    this.cleanupFaceCanvasBuffer();
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    this.faceCanvasBuffer = null;
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    this.cleanupFaceCanvasBuffer = null;
  }

  public render() {
    const {
      assets,
      buttonStyles,
      localizations,
      poseOrder,
      videoConstraints,
      handleFailed,
      debug,
      styles,
      uploadProgress,
    } = this.props;
    const {
      profileSize,
      activeTrigger,
      manualVisible,
      videoWidthOverride,
      videoHeightOverride,
      autoCaptureDisabled,
      currentStep,
    } = this.state;

    const isSafari = deviceIsSafari();
    const shouldUseWebrtc =
      this.props.enableVideoRecording && MediaRecorderManager.currentClient?.type === 'stream';
    const isLoading =
      (this.state.showPreloadingSpinnerVideo && (isSafari || shouldUseWebrtc)) ||
      this.state.showPreloadingSpinnerForUserMedia;

    return (
      <>
        <SelfieScannerBase
          audioEnabled={this.props.audioEnabled}
          autoCaptureDisabled={autoCaptureDisabled}
          assets={assets}
          buttonStyles={buttonStyles}
          localizations={localizations}
          currentStep={currentStep}
          poseOrder={poseOrder}
          activeTrigger={activeTrigger}
          videoWidthOverride={videoWidthOverride}
          videoHeightOverride={videoHeightOverride}
          setWebcamRef={this.setWebcamRef}
          videoConstraints={videoConstraints}
          handleUserMedia={this.handleUserMedia}
          handleFailed={handleFailed}
          handleManualCapture={this.handleManualCapture}
          manualVisible={manualVisible}
          profileSize={profileSize}
          debug={debug}
          styles={styles}
          showPreloadingSpinner={isLoading}
          showPreloadingHint={isLoading}
          uploadProgress={this.props.enableVideoRecording ? uploadProgress : undefined}
          enableMobilePendingExperienceForWeb={this.props.enableMobilePendingExperienceForWeb}
          featureFlags={this.props.featureFlags}
          allowNarrowCanvas={this.state.allowNarrowCanvas}
        />
        <VideoDimensionsTracker dimensions={this.state.videoDimensions} />
      </>
    );
  }

  private setWebcamRef = (ref: Webcam & HTMLVideoElement) => {
    this.refWebcam = ref;

    if (this.refWebcam?.video) {
      this.refWebcam.video.addEventListener('canplay', this.resizeVideo);

      // There is a longstanding React bug where the muted attribute is not
      // propagated to the DOM <video> element. This can cause permissions
      // related issues.
      // Sample fix: https://stackoverflow.com/a/66153842
      // Github issue: https://github.com/facebook/react/issues/6544#issuecomment-705702546
      // Remove if upstream fix merged: https://github.com/mozmorris/react-webcam/issues/332
      this.refWebcam.video.defaultMuted = true;
      this.refWebcam.video.muted = true;
    }
  };

  private resizeVideo = () => {
    if (this.refWebcam?.video == null) {
      return;
    }
    const { clientWidth, videoHeight, videoWidth } = this.refWebcam.video;
    this.tickCountWhenFeedIsReady = this.state.tickCount;
    if (videoHeight > videoWidth) {
      // If the video is portrait, force it to be square aspect ratio
      this.setState({ videoHeightOverride: clientWidth });
    } else {
      // If the video is landscape, set the width to be large enough so the height is at least 250px (VIDEO_ELEMENT_HEIGHT)
      const videoAspectRatio = videoWidth / videoHeight;
      const newVideoWidth = Math.floor(
        Math.max(clientWidth, VIDEO_ELEMENT_HEIGHT * videoAspectRatio),
      );

      this.setState({ videoWidthOverride: newVideoWidth });
    }
  };

  /**
   * Handles browser compatibility issues (including taking too long).
   * It can take too long if this browser doesn't support the right WebGL extensions.
   */
  private runTick = async () => {
    const startTime = new Date().getTime();

    try {
      // we wait 5 ticks (this is a minimum of 0.5 seconds, but can be slower based on requestAnimationFrame)
      // before calling FaceAPI detection to let the feed reach a normal state first to avoid weirdness
      // such as extremely tiny feed. through testing on an iphone 10 XR, this number can go as low as 1
      // without bugging out with the tiny feed, which may suggest that only the very first frame is problematic,
      // but a few more frames were added to be more generous in case other devices take longer to let the feed
      // get normal
      if (
        (!isIOS() && !isIOSDoordashUserAgent()) ||
        this.state.tickCount > this.tickCountWhenFeedIsReady + 5
      ) {
        await this.runDetection();
      }
    } catch (err) {
      captureException(err, 'info', generateWebGLDebugInfo(), { jira: 'PER-19877' });
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      this.props.handleFailed(err, 'capture');
      return;
    }

    const processingTime = new Date().getTime() - startTime;
    if (
      this.state.tickCount === 0 &&
      this.props.detectTimeout != null &&
      processingTime > this.props.detectTimeout &&
      !this.state.autoCaptureDisabled
    ) {
      // NOTE: Device incompatibility means this can be >10 seconds (1k over 3 months).
      // In that case, we fallback to stillfie. It's too hard to when this will happen.
      this.props.handleSlowFaceDetection();
      return;
    }

    const nextFrameDelay = Math.max(0, DETECT_INTERVAL - processingTime);

    this.setState(({ tickCount }) => ({
      tickCount: tickCount + 1,
    }));

    if (this.refWebcam?.video?.videoHeight != null && this.refWebcam?.video?.videoWidth != null) {
      this.setState({
        videoDimensions: {
          width: this.refWebcam.video.videoWidth,
          height: this.refWebcam.video.videoHeight,
        },
      });
    }

    // NOTE: Make sure refWebcam exists at this point in time because the component
    // may have unmounted before runTick resolves
    if (!this.state.hasSentSelfieData) {
      if (!this.state.hasUnmounted && this.refWebcam) {
        this.timerDetect = window.setTimeout(() => {
          if (window.requestAnimationFrame != null) {
            // setTimeout effectively sets a "floor" for rAF. This will tick
            // every 100ms at fastest, and will slow down via rAF if needed.
            window.requestAnimationFrame(this.runTick);
          } else {
            this.runTick();
          }
        }, nextFrameDelay);
      } else if (this.state.hasUnmounted && this.refWebcam) {
        // NOTE: logging to troubleshoot issue with repeated selfie poses
        // this.state.hasUnmounted && !this.refWebcam is an expected exit path, others indicate an issue
        try {
          captureMessage(
            'SelfieScanner runTick finished before hasSentSelfieData was true',
            'warning',
            {
              hasUnmounted: this.state.hasUnmounted,
              refWebcamExists: this.refWebcam != null,
              refWebcamVideoExists: this.refWebcam?.video != null,
              videoHeight: this.refWebcam?.video?.height,
              videoWidth: this.refWebcam?.video?.width,
            },
          );
        } catch (err) {
          captureMessage(
            'SelfieScanner runTick finished early and failed to log reason',
            'warning',
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            err,
          );
        }
      }
    }
  };

  private logCameraInspectionSnapshot = () => {
    if (this.cameraInspectionSnapshots.length >= 5) return;

    // capture a snapshot every other tick to reduce false rejections due to low-end cameras
    if (this.shouldCaptureCameraInspectionSnapshots) {
      const snapshotHash = getStreamStabilitySnapshotHash(
        this.canvasBuffer?.toDataURL('image/jpeg'),
      );

      if (snapshotHash != null) {
        this.cameraInspectionSnapshots.push(snapshotHash);
        this.shouldCaptureCameraInspectionSnapshots = false;
      }
    } else {
      this.shouldCaptureCameraInspectionSnapshots = true;
    }
  };

  private completeCameraInspection = () => {
    if (this.cameraInspectionData) {
      this.cameraInspectionData.streamStability = generateStreamStability({
        snapshots: this.cameraInspectionSnapshots,
        includeSmallImages: true,
      });
    }
  };

  private handleUserMedia = async (stream: MediaStream) => {
    clearTimeout(this.timerShowSpinnerForNonVideo);
    this.setState({ showPreloadingSpinnerForUserMedia: false });
    if (this.cameraInspectionData || !stream) {
      return;
    }

    this.cameraInspectionData = {
      ...DEFAULT_CAMERA_INSPECTION_DATA,
    };

    this.cameraInspectionData = {
      ...this.cameraInspectionData,
      ...generateStreamSettingsData(stream),
    };

    // This has to be done in 2 separate assignments because some of the data is gathered async;
    // See front-end/lib/hooks/useCameraInspection.ts#58
    // Distribution of time taken for this to complete: https://app.amplitude.com/analytics/persona/chart/r5f9c9t4
    // Data taken over 2 days, mostly under 1 second - but this can take longer.
    //    Less occurences as time increases up to 9 seconds, with a jump to max time of 18 seconds
    this.cameraInspectionData = {
      ...this.cameraInspectionData,
      ...(await generateDeviceData(stream)),
    };

    if (this.props.enableVideoRecording && !this.state.mediaRecorderSubId) {
      this.initMediaRecorderClient(stream, this.props.videoSessionJwt);
    }
  };

  private completeSelfieScan = (capturedVideoData?: CapturedVideoData) => {
    // Last tick to verify and update the hint
    this.runTick();

    this.completeCameraInspection();
    clearTimeout(this.timerDetect);
    clearTimeout(this.timerManualShow);

    const startTimeISO = this.state.startTime
      ? new Date(this.state.startTime).toISOString()
      : undefined;

    this.props.handleComplete({
      capturedSelfies: this.props.capturedSelfies,
      captureStartedAt: startTimeISO,
      cameraInspectionData: this.cameraInspectionData,
      capturedVideoData: capturedVideoData,
    });

    this.setState({
      ...this.state,
      hasSentSelfieData: true,
      manualVisible: false,
    });
  };

  private runDetection = async () => {
    const { capturedSelfies, requireHigherAccuracyCapture, poseOrder, poseConfigs, featureFlags } =
      this.props;

    if (
      !this.refWebcam ||
      !FaceApiClient.isLoaded() ||
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      !FaceApiClient.isMediaLoaded(this.refWebcam.video) ||
      !this.state.readyToCapture
    ) {
      return;
    }

    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    const { clientHeight, clientWidth } = this.refWebcam.video;
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    const { videoHeight, videoWidth } = this.refWebcam.video;

    // May have re-routed to another page while async tasks were running
    if (!this.refWebcam || !this.canvasBuffer) {
      return;
    }

    // 0. Capture image to process and save
    this.canvasBuffer.width = videoWidth;
    this.canvasBuffer.height = videoHeight;
    const canvasContext = this.canvasBuffer.getContext('2d', {
      willReadFrequently: true,
      alpha: false, // supporting transparency is more expensive
    });

    if (canvasContext == null) {
      captureMessage(
        'Canvas getContext returned null during runDetection',
        'warning',
        this.getSentryErrorContext(),
      );
    }

    canvasContext?.clearRect(0, 0, this.canvasBuffer.width, this.canvasBuffer.height);
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    canvasContext?.drawImage(this.refWebcam.video, 0, 0, videoWidth, videoHeight);

    // 1. Get face count
    const detection = await FaceApiClient.detectFaces(this.canvasBuffer, {
      inputSize: FACE_DETECTION_INPUT_SIZE,
      scoreThreshold: FACE_DETECTION_THRESHOLD,
    });

    const faceBox = detection.length === 1 ? detection[0].box : undefined;

    let horizontalAngle = 0;
    let landmarks = null;

    // May have re-routed to another page while async tasks were running
    if (!this.refWebcam || !this.canvasBuffer) {
      return;
    }

    // 2. Detect face landmarks and infer angle
    if (faceBox != null) {
      try {
        this.logCameraInspectionSnapshot();
      } catch (e) {
        // Camera inspection snapshot shouldn't block capture
        captureException(e);
      }

      // May have re-routed to another page while async tasks were running
      if (!this.refWebcam || !this.faceCanvasBuffer || !this.canvasBuffer) {
        return;
      }

      this.faceCanvasBuffer.width = Math.floor(faceBox.width);
      this.faceCanvasBuffer.height = Math.floor(faceBox.height);

      const context = this.faceCanvasBuffer.getContext('2d', {
        willReadFrequently: true,
        alpha: false, // supporting transparency is more expensive
      }) as CanvasRenderingContext2D;
      context?.clearRect(0, 0, this.faceCanvasBuffer.width, this.faceCanvasBuffer.height);
      context?.drawImage(
        // Use Math.floor to avoid floats in canvas dimensions, which hurts performance.
        // https://developer.mozilla.org/en-US/docs/Web/API/Canvas_API/Tutorial/Optimizing_canvas#avoid_floating-point_coordinates_and_use_integers_instead
        this.canvasBuffer,
        Math.floor(faceBox.x),
        Math.floor(faceBox.y),
        Math.floor(faceBox.width),
        Math.floor(faceBox.height),
        0,
        0,
        Math.floor(faceBox.width),
        Math.floor(faceBox.height),
      );

      // May have re-routed to another page while async tasks were running
      if (!this.refWebcam || !this.faceCanvasBuffer) {
        return;
      }

      landmarks = await FaceApiClient.detectLandmarks(this.faceCanvasBuffer);
      horizontalAngle = horizontalAngleFromLandmarks(landmarks);
      this.drawDebugMarks(faceBox, landmarks);
    }

    // May have re-routed to another page while async tasks were running
    if (!this.refWebcam) {
      return;
    }

    const currentStep = getCurrentSelfieStep(capturedSelfies, poseOrder);
    const faceCount = detection.length;
    const faceHeight = faceBox != null ? faceBox.height : 0;
    const faceWidth = faceBox != null ? faceBox.width : 0;
    const elapsedTime = Date.now() - this.state.startTime;

    // 3. Run trigger client to infer active hint
    this.hintTriggerClient.runTick({
      capturedSelfies,
      currentStep,
      faceCount,
      horizontalAngle,
      faceHeight,
      faceWidth,
      manualVisible: this.state.manualVisible,
      videoHeight,
      videoWidth,
      requireHigherAccuracyCapture,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      faceBox,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      landmarks,
      elapsedTime,
    });

    const activeTrigger = this.hintTriggerClient.getTriggered();

    // 4. Update captured selfies
    if (FOCUSED_TRIGGER_TYPES.indexOf(activeTrigger.getType()) === -1) {
      this.props.clearCapturedSelfies();
    } else {
      const sourceImage = this.canvasBuffer.toDataURL('image/jpeg', 92);

      if (sourceImage == null) {
        // [PER-20180] We sometimes observe empty data URLs on iOS Safari
        captureMessage(
          'Selfie autocapture captured an empty image',
          'warning',
          this.getSentryErrorContext(),
          {
            jira: 'PER-20180',
          },
        );
        this.props.handleFailed('Canvas buffer data URL is empty', 'capture');
      }

      const activeTriggerType = activeTrigger.getType();
      const pose = triggerTypeToSelfiePose(activeTriggerType);
      const poseHasNotBeenSaved = pose != null && getSelfiePose(capturedSelfies, pose) == null;
      const currentPoseConfig = currentStep && getSelfieStepConfig(currentStep, poseConfigs);
      const isAutoCaptureEnabled =
        currentPoseConfig?.['auto-capture-enabled'] !== false ||
        !featureFlags['inquiry:dynamic-selfie-config'];

      if (poseHasNotBeenSaved && isAutoCaptureEnabled) {
        switch (activeTriggerType) {
          case SelfieTriggerType.CenterCapture: {
            this.props.saveCapturedSelfie(
              SelfieStep.Center,
              sourceImage,
              CaptureMethod.Auto,
              new Date().toISOString(),
            );
            break;
          }
          case SelfieTriggerType.LeftCapture: {
            this.props.saveCapturedSelfie(
              SelfieStep.Left,
              sourceImage,
              CaptureMethod.Auto,
              new Date().toISOString(),
            );
            break;
          }
          case SelfieTriggerType.RightCapture: {
            this.props.saveCapturedSelfie(
              SelfieStep.Right,
              sourceImage,
              CaptureMethod.Auto,
              new Date().toISOString(),
            );
            break;
          }
        }
      }
    }

    if (
      this.props.featureFlags?.['inquiry:enable-custom-1'] &&
      !isMobile() &&
      !isSafari() &&
      !HolderClient.isWebView &&
      this.refWebcam?.video &&
      this.state.tickCount <= 20
    ) {
      try {
        const results = await CustomModelClient.detect(this.refWebcam.video);
        // only track results with a score above 0.75 to reduce noise
        const filteredResults = results.filter((result) => result.score >= 0.75);
        if (filteredResults.length > 0) {
          TrackingEventsClient.track(
            {
              type: 'inquiry.model.detect',
              data: filteredResults.map((result) => ({
                name: 'custom-1',
                box: result.box,
                score: result.score,
                frame: this.state.tickCount,
              })),
              stepName: 'selfie',
              pathName: '/inquiry/selfie/record',
            },
            this.props.featureFlags,
          );
        }
      } catch (e) {
        CustomModelClient.errored = true;
        captureException(e instanceof Error ? e : new Error(JSON.stringify(e)), 'info');
      }
    }

    this.setState((prevState) => ({
      ...prevState,
      activeTrigger,
      // Leave padding
      profileSize: Math.min(clientWidth, clientHeight) - 8,
    }));
  };

  private handleManualCapture = () => {
    const { capturedSelfies, poseOrder } = this.props;
    const currentStep = getCurrentSelfieStep(capturedSelfies, poseOrder);

    if (this.refWebcam?.video) {
      // Redraw latest video frame to canvas to avoid potentially capturing duplicate frames.
      // This is the Selfie equivalent of bufferImageWithOverlayWindow.
      const { videoHeight, videoWidth } = this.refWebcam.video;
      this.canvasBuffer.width = videoWidth;
      this.canvasBuffer.height = videoHeight;
      const canvasContext = this.canvasBuffer.getContext('2d', {
        willReadFrequently: true,
        alpha: false, // supporting transparency is more expensive
      });

      if (canvasContext == null) {
        captureMessage(
          'Canvas getContext returned null during handleManualCapture',
          'warning',
          this.getSentryErrorContext(),
          {
            videoEnabled: (!!this.props.enableVideoRecording).toString(),
            videoCaptureMethod: MediaRecorderManager.currentClient?.type,
          },
        );
      }

      canvasContext?.clearRect(0, 0, this.canvasBuffer.width, this.canvasBuffer.height);
      canvasContext?.drawImage(this.refWebcam.video, 0, 0, videoWidth, videoHeight);
    }

    const sourceImage = this.canvasBuffer.toDataURL('image/jpeg', 92);

    if (sourceImage == null || sourceImage.length < BLANK_CAPTURE_BYTE_SIZE) {
      captureMessage(
        'Selfie manual capture captured a potentially blank image',
        'warning',
        this.getSentryErrorContext(),
        {
          videoEnabled: (!!this.props.enableVideoRecording).toString(),
          videoCaptureMethod: MediaRecorderManager.currentClient?.type,
        },
      );
      // Force user to retake
      this.props.handleFailed(
        'Selfie manual capture captured a potentially blank image',
        'capture',
      );
    }

    currentStep &&
      this.props.saveCapturedSelfie(
        currentStep,
        sourceImage,
        CaptureMethod.Manual,
        new Date().toISOString(),
      );
  };

  private drawDebugMarks(faceBox: Box, landmarks: FaceLandmarks68) {
    if (!this.props.debug) {
      return;
    }

    const debugCanvas = document.getElementById('debugLandmarks') as HTMLCanvasElement;

    const videoWidth = this.canvasBuffer.width;
    const videoHeight = this.canvasBuffer.height;
    debugCanvas.width = videoWidth;
    debugCanvas.height = videoHeight;
    const context = debugCanvas.getContext('2d');

    if (context == null) return;

    context.globalAlpha = 0.4;
    context.drawImage(this.canvasBuffer, 0, 0);
    context.globalAlpha = 1.0;

    const xOffset = faceBox.left;
    const yOffset = faceBox.top;

    context.fillStyle = '#000000';
    for (const point of landmarks.positions) {
      context.fillRect(point.x - 2 + xOffset, point.y - 2 + yOffset, 4, 4);
    }

    const nosePoint = landmarks.getNose()[3];
    context.fillStyle = '#0000ff';
    context.fillRect(nosePoint.x - 8 + xOffset, nosePoint.y - 8 + yOffset, 16, 16);

    context.strokeStyle = '#ff0000';
    context.lineWidth = 8.0;
    const squareSize = Math.min(videoWidth, videoHeight) * 0.4;
    const smallestDimension = Math.min(videoWidth, videoHeight);
    context.rect(
      videoWidth / 2 - squareSize / 2,
      videoHeight / 2 - squareSize / 2,
      squareSize,
      squareSize,
    );
    context.stroke();
    context.strokeStyle = '#00ff00';
    context.rect(
      videoWidth / 2 - smallestDimension / 2,
      videoHeight / 2 - smallestDimension / 2,
      smallestDimension,
      smallestDimension,
    );
    context.stroke();
  }

  private initMediaRecorderClient(stream: MediaStream, videoSessionJwt?: string | null) {
    const mediaRecorderClient = MediaRecorderManager.getNewClient(this.props.videoCaptureMethods);

    if (this.props.enableVideoRecording && !mediaRecorderClient.isSupported()) {
      this.props.handleFailed('MediaRecorder not supported', 'load');
      return;
    }

    const mediaRecorderSubId = mediaRecorderClient.init({
      stream,
      video: this.refWebcam.video,
      jwt: videoSessionJwt,
      maxRecordingTime: VIDEO_MAX_RECORDING_TIME,
      errorOnSlowComplete: this.props.featureFlags['inquiry:video-error-on-slow-complete'],
      enableRestartIce: this.props.featureFlags['inquiry:video-restart-ice'],
      onRecordingComplete: (capturedVideoData: CapturedVideoData, elapsedTime: number) => {
        // MediaRecorder stop events might be duplicated, so if the state already exists then just return
        if (this.state.capturedVideoData) {
          return;
        }

        EventsClient.logAmplitude('selfie.video.stop', {
          elapsedTime,
          type: mediaRecorderClient.type,
          chunksLength: capturedVideoData.type === 'upload' ? capturedVideoData.chunks?.length : 0,
        });

        if (
          capturedVideoData.type === 'upload' &&
          (capturedVideoData.chunks == null || capturedVideoData.chunks.length <= 0)
        ) {
          if (MediaRecorderManager.videoCanBeReattempted()) {
            this.props.handleRestartStep?.();
          }
        } else {
          this.setState({ capturedVideoData });
          this.completeSelfieScan(capturedVideoData);
        }
      },
      onError: (e: string | Error) => {
        // Destroy the client so that it doesn't trigger subscribers anymore
        MediaRecorderManager.destroy('selfie');

        this.props.handleRestartStep?.();

        EventsClient.logAmplitude('selfie.video.errored', {
          message: typeof e === 'string' ? e : e?.message,
        });

        captureException(
          e instanceof Error ? e : new Error(`Unexpected error: ${JSON.stringify(e)}`),
          'error',
        );
      },
      onStart: (isRestart: boolean) => {
        this.setState({ showPreloadingSpinnerVideo: false, readyToCapture: true });
        // Force restart the whole process if max video recording time was exceeded
        if (isRestart && !this.state.hasStoppedRecording) {
          this.props.clearCapturedSelfies();
        }
        EventsClient.logAmplitude('selfie.video.start', {
          isRestart,
          type: mediaRecorderClient.type,
        });
      },
      onTimeExceeded: () => {
        EventsClient.logAmplitude('selfie.video.timeExceeded');
        const { capturedSelfies, poseOrder } = this.props;
        // Only throw back to the prompt page if all the poses have NOT been collected
        if (getCurrentSelfieStep(capturedSelfies, poseOrder) != null) {
          this.props.handleRestartStep?.({ timeExceeded: true });
        }
      },
    });
    MediaRecorderManager.recordVideoAttempt();
    mediaRecorderClient.startRecording();

    EventsClient.logAmplitude('selfie.video.initialized', {
      type: mediaRecorderClient.type,
      attempt: MediaRecorderManager.videoAttempts,
    });

    this.setState({ mediaRecorderSubId });
  }

  private getSentryErrorContext() {
    return {
      tickCount: this.state.tickCount,
      elapsedTime: Date.now() - this.state.startTime,
    };
  }
}
